import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { baseUrl, formatDate, formatDateForFile } from "../../comman/config";
import Loader from '../../components/Loader';
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/solid";
import { DateSessionReportFilter } from "../../components/Filter/DateSessionReportFilter";
import { isFilterActive, matchFilterText, patchFilter } from "../../components/Filter/columnFilter";
import ColumnFilterRow from "../../components/Filter/ColumnFilterRow";
import { ItemName, itemSearchText, itemExportName } from "../../components/ItemName";
import { useIsMobile } from "../../comman/useIsMobile";

// Master Report column layout (per the "Master Report" sheet). Each measure group holds its
// Std / Planned / Production / Historical / Actual sub-columns. Rows come from the ticked Raw
// Report rows; values are already mapped server-side (/reports/new_master_report).
// `src` names the screen a Std column is quoted from; it shows as the header's tooltip, so the
// reader can tell a Production Estimation standard from a Consumption Master one without leaving
// the report. Every Std column carries one.
const GROUPS = [
    { key: 'count', label: 'Count', cols: [
        { k: 'count_planned', h: 'Planned', d: 0 }, { k: 'count_production', h: 'Production', d: 0 },
        { k: 'count_historical', h: 'Historical', d: 0 }, { k: 'pass_issued', h: 'Pass Issued', d: 0 },
        { k: 'final_dish_count', h: 'Final Dish count', d: 0 } ] },
    { key: 'ras', label: 'RAS', cols: [
        { k: 'ras_std', h: 'Std', d: 2, src: 'Source: Production Estimation > Standard > RAS' }, { k: 'ras_planned', h: 'Planned', d: 2 }, { k: 'ras_production', h: 'Production', d: 2 },
        { k: 'ras_actual', h: 'Actual', d: 2 }, { k: 'ras_historical', h: 'Historical', d: 2 } ] },
    { key: 'fgp', label: 'FG / Person', cols: [
        { k: 'fgp_std', h: 'Std', d: 2, src: 'Source: Production Estimation > Standard > FG / Person' }, { k: 'fgp_planned', h: 'Planned', d: 2 }, { k: 'fgp_production', h: 'Production', d: 2 },
        { k: 'fgp_actual', h: 'Actual', d: 2 }, { k: 'fgp_historical', h: 'Historical', d: 2 } ] },
    { key: 'ratio', label: 'RM to FG Ratio', cols: [
        { k: 'ratio_std', h: 'Std', d: 3, src: 'Source: Consumption Master > RM to FG' }, { k: 'ratio_planned', h: 'Planned', d: 3 },
        { k: 'ratio_production', h: 'Production', d: 3 }, { k: 'ratio_historical', h: 'Historical', d: 3 } ] },
    { key: 'bowl', label: '1 Bowl Qty', cols: [ { k: 'bowl_std', h: 'Std', d: 2, src: 'Source: Consumption Master > Bowl Qty' }, { k: 'bowl_actual', h: 'Actual', d: 2 } ] },
    { key: 'rmbowl', label: 'RM to Bowl Ratio', cols: [ { k: 'rmbowl_std', h: 'Std', d: 3, src: 'Source: Consumption Master > RM to Bowl' }, { k: 'rmbowl_actual', h: 'Actual', d: 3 } ] },
    { key: 'ppb', label: 'Avg. Persons Per Bowl', cols: [ { k: 'ppb_std', h: 'Std', d: 0, src: 'Source: Consumption Master > Avg / Bowl' }, { k: 'ppb_actual', h: 'Actual', d: 0 } ] },
    { key: 'density', label: 'Density', cols: [ { k: 'density_std', h: 'Std (Consumption Master)', d: 3, src: 'Source: Consumption Master > Density' }, { k: 'density_live', h: 'Live (Meal Master)', d: 3 } ] },
    { key: 'excess', label: 'Excess / Shortage', cols: [
        { k: 'excess_kgs', h: 'Physical Qty (Kgs)', d: 2 }, { k: 'excess_persons', h: 'Physical Persons (Nos)', d: 0 },
        { k: 'net_kgs', h: 'Net Qty (Kgs)', d: 2 }, { k: 'net_persons', h: 'Net Persons (Nos)', d: 0 } ] },
    { key: 'rm', label: 'Raw Material (RM)', cols: [ { k: 'rm_planned', h: 'Planned', d: 2 }, { k: 'rm_actual', h: 'Actual', d: 2 } ] },
    { key: 'fg', label: 'Finished Goods (FG)', cols: [ { k: 'fg_planned', h: 'Planned', d: 2 }, { k: 'fg_actual', h: 'Actual', d: 2 } ] },
];

const dec = (v, d) => {
    if (v === null || v === undefined || v === '') return '';
    const n = parseFloat(v);
    return isNaN(n) ? '' : n.toFixed(d);
};

const th = "px-2 py-1 border border-black text-center";
const td = "px-2 py-1 border border-black text-center";

// Thick dark divider between sections (mirrors the Raw Report / Production Estimation pattern).
const SECTION_DIVIDER = '3px solid #111827';

// Frozen (freeze-pane) identity columns up to Menu Item. width drives the sticky left offset.
const FROZEN = [
    { key: 'date', width: 96 },
    { key: 'session', width: 96 },
    { key: 'menu_item', width: 150 },
];
const FROZEN_LEFT = FROZEN.reduce((acc, c, i) => { acc[c.key] = i === 0 ? 0 : acc[FROZEN[i - 1].key] + FROZEN[i - 1].width; return acc; }, {});
// enabled=false returns a bare background, no sticky and no fixed width: on a phone the three
// frozen columns are 342px of a ~360-412px viewport, so freezing them left no room for the measures.
const frozenStyle = (key, zIndex, bg, enabled = true) => (enabled ? {
    position: 'sticky', left: FROZEN_LEFT[key], zIndex, backgroundColor: bg,
    width: FROZEN.find(c => c.key === key).width, minWidth: FROZEN.find(c => c.key === key).width, maxWidth: FROZEN.find(c => c.key === key).width,
} : { backgroundColor: bg });

function NewMasterReport() {
    const [rows, setRows] = useState([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [session, setSession] = useState('');
    const [item, setItem] = useState('');
    const [method, setMethod] = useState([]);
    // Events picked in the filter's multi-select -- forwarded so the report covers those events
    // only, rather than every date between the first event's start and the last one's end.
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    // Freeze-pane is desktop only -- see frozenStyle.
    const isMobile = useIsMobile();
    const frozen = (key, zIndex, bg) => frozenStyle(key, zIndex, bg, !isMobile);
    // Hidden LEAF columns, by column key. Group visibility is derived from this rather than stored
    // alongside it -- one source of truth means a group can never claim to be shown while all its
    // columns are hidden, or the reverse.
    const [hiddenCols, setHiddenCols] = useState(() => new Set());
    // Which groups are expanded in the Show Columns dropdown. Collapsed by default so the list
    // opens as a short set of group names rather than every column at once.
    const [openGroups, setOpenGroups] = useState(() => new Set());
    const toggleOpenGroup = (key) => setOpenGroups(prev => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
    });
    const [showColsOpen, setShowColsOpen] = useState(false);
    const [filters, setFilters] = useState({}); // { [colKey]: { op, v1, v2 } }
    const dropdownRef = useRef(null);

    useEffect(() => { getList(); }, [fromDate, toDate, session, item]);

    // Close the "Show Columns" dropdown on outside click.
    useEffect(() => {
        const onClick = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowColsOpen(false); };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    // Args default to the current state so the useEffect above can call this with none. Submit
    // passes the filter's values explicitly -- state is set in the same tick and would still be
    // stale here, so reading state would fetch the previous filter's data.
    const getList = async (f = fromDate, t = toDate, s = session, i = item, m = method, ev = events) => {
        setLoading(true);
        try {
            const url = `${baseUrl}/reports/new_master_report?fromDate=${f}&toDate=${t}` +
                `&session=${encodeURIComponent(JSON.stringify(s))}&item=${encodeURIComponent(JSON.stringify(i))}` +
                `&event=${encodeURIComponent(JSON.stringify(ev ?? []))}&itemFilter=in`;
            const response = await axios.get(url);
            setRows(response.data.data ?? []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handelFilterChange = (f, t, s, i, m, pickedEvents) => {
        setFromDate(f); setToDate(t); setSession(s); setItem(i); setMethod(m ?? []); setEvents(pickedEvents ?? []);
    };

    // Method filter: match the DISPLAYED method (method_id), so a Kgs mirror row of a litres/nos
    // item is treated as "Kgs" like the Method column shows. Empty selection = all.
    const selectedMethodIds = (method ?? []).map(m => Number(m.value));
    const filteredRows = selectedMethodIds.length
        ? rows.filter(r => selectedMethodIds.includes(Number(r.method_id)))
        : rows;

    const ALL_COL_KEYS = GROUPS.flatMap(g => g.cols.map(c => c.k));

    const colShown = (c) => !hiddenCols.has(c.k);
    // A group ticks when every column under it is shown, and sits indeterminate while only some are.
    const groupState = (g) => {
        const shown = g.cols.filter(colShown).length;
        return { all: shown === g.cols.length, none: shown === 0, some: shown > 0 && shown < g.cols.length };
    };

    const toggleCol = (key) => setHiddenCols(prev => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
    });
    // Ticking a group shows all of its columns; unticking hides all of them. Individual columns can
    // then be unticked underneath, which is what leaves the group indeterminate.
    const toggleGroup = (g) => setHiddenCols(prev => {
        const next = new Set(prev);
        const { all } = groupState(g);
        g.cols.forEach(c => (all ? next.add(c.k) : next.delete(c.k)));
        return next;
    });
    const allVisible = ALL_COL_KEYS.every(k => !hiddenCols.has(k));
    const toggleAll = (checked) => setHiddenCols(checked ? new Set() : new Set(ALL_COL_KEYS));

    // Groups keep only their visible columns, so headers, body, filters and the export all follow
    // from this one list without each having to re-check visibility.
    const shownGroups = GROUPS
        .map(g => ({ ...g, cols: g.cols.filter(colShown) }))
        .filter(g => g.cols.length > 0);

    // Inline per-column filters, same operator set as the Raw Report. The flat column list mirrors
    // the table's leaf columns exactly -- 4 identity columns then every visible group column -- so
    // the filter row lines up with the header and body. `text` returns what the cell renders, so
    // you filter on precisely what you can see.
    const filterCols = [
        { key: 'date', text: (r) => formatDate(r.date), frozen: true },
        { key: 'session', text: (r) => r.session_name ?? '', frozen: true },
        // Reads in Gujarati, matches on either language -- see components/ItemName.
        { key: 'menu_item', text: itemSearchText, frozen: true },
        { key: 'method', text: (r) => r.method ?? '' },
        ...shownGroups.flatMap(g => g.cols.map((c, ci) => ({
            key: c.k, text: (r) => dec(r[c.k], c.d), divider: ci === 0,
        }))),
    ];
    const setColFilter = (key, patch) => setFilters(prev => patchFilter(prev, key, patch));
    const activeFilterCols = filterCols.filter(c => isFilterActive(filters[c.key]));
    const visibleRows = filteredRows.filter(r => activeFilterCols.every(c => matchFilterText(c.text(r), filters[c.key])));

    // The file is the table: the same two header rows with the same section and column labels, the
    // same merges the screen's colSpan and rowSpan produce, the same columns Show Columns left
    // visible, and the same rows the inline filters left. Before, the section label was written
    // into its first column and the rest of the band left blank with no merge, so in the sheet a
    // column such as "Std" or "Planned" sat under an empty cell with nothing to say which measure
    // it belonged to; and the body ignored the inline filters, so the file could hold rows the
    // screen did not.
    const handleExport = () => {
        const idCols = ['Date', 'Session', 'Menu Item', 'Method'];
        const header1 = [...idCols];
        const header2 = idCols.map(() => '');
        // Identity columns span both header rows, as they do on screen (rowSpan={2}).
        const merges = idCols.map((_, i) => ({ s: { r: 0, c: i }, e: { r: 1, c: i } }));
        let col = idCols.length;
        shownGroups.forEach(g => {
            g.cols.forEach((c, idx) => { header1.push(idx === 0 ? g.label : ''); header2.push(c.h); });
            // A one-column section needs no merge; Excel rejects a range that starts where it ends.
            if (g.cols.length > 1) merges.push({ s: { r: 0, c: col }, e: { r: 0, c: col + g.cols.length - 1 } });
            col += g.cols.length;
        });
        const body = visibleRows.map(r => {
            const line = [formatDate(r.date), r.session_name, itemExportName(r), r.method];
            shownGroups.forEach(g => g.cols.forEach(c => line.push(dec(r[c.k], c.d))));
            return line;
        });
        const ws = XLSX.utils.aoa_to_sheet([header1, header2, ...body]);
        ws['!merges'] = merges;
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Master Report");
        // Name the file for the range it covers, not just where it starts. This carried the FROM
        // date alone, so a report for 30/07/2023 to 30/07/2026 saved as Master_Report_30_07_2023
        // and was indistinguishable from a single day's. Dates are formatted for a filename rather
        // than displayed -- see formatDateForFile; formatDate's slashes never survive the save.
        const range = [fromDate, toDate].filter(Boolean).map(formatDateForFile).join('_to_');
        XLSX.writeFile(wb, `Master_Report${range ? '_' + range : ''}.xlsx`);
    };

    return (
        <div className="p-4">
            {loading && <Loader />}
            <div className="flex justify-between items-center py-4 px-6">
                <h1 className="text-2xl font-bold">Master Report</h1>
                <div className="flex items-center gap-3">
                    <div className="relative" ref={dropdownRef}>
                        <button onClick={() => setShowColsOpen(o => !o)}
                            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700">Show Columns</button>
                        {/* Group, then its columns nested underneath. Ticking a group takes all of its
                            columns with it; unticking one column afterwards leaves the group
                            indeterminate, which shows as a dash rather than a tick. */}
                        {showColsOpen && (
                            <div className="absolute right-0 mt-1 w-72 max-h-96 overflow-auto bg-white border border-gray-300 rounded-md shadow-lg z-30 p-2">
                                <label className="flex items-center gap-2 px-2 py-1 text-sm font-semibold text-gray-900 border-b border-gray-200 cursor-pointer">
                                    <input type="checkbox" checked={allVisible} onChange={(e) => toggleAll(e.target.checked)} />
                                    All
                                </label>
                                {GROUPS.map(g => {
                                    const { all, some } = groupState(g);
                                    return (
                                        <div key={g.key} className="py-1 border-b border-gray-100 last:border-b-0">
                                            <div className="flex items-center gap-2 px-2 py-1 text-sm font-semibold text-gray-900 hover:bg-gray-100">
                                                <input
                                                    type="checkbox"
                                                    checked={all}
                                                    // indeterminate is a DOM property, not an attribute -- React
                                                    // cannot set it through JSX, so it goes on via the ref.
                                                    ref={(el) => { if (el) el.indeterminate = some; }}
                                                    onChange={() => toggleGroup(g)} />
                                                {/* The name expands the group; only the box changes what is shown. */}
                                                <button type="button" onClick={() => toggleOpenGroup(g.key)}
                                                    className="flex-1 flex items-center justify-between text-left cursor-pointer">
                                                    <span>{g.label}</span>
                                                    {openGroups.has(g.key)
                                                        ? <ChevronUpIcon className="w-4 h-4 shrink-0 text-gray-500" />
                                                        : <ChevronDownIcon className="w-4 h-4 shrink-0 text-gray-500" />}
                                                </button>
                                            </div>
                                            {openGroups.has(g.key) && g.cols.map(c => (
                                                <label key={c.k} className="flex items-center gap-2 pl-8 pr-2 py-0.5 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer">
                                                    <input type="checkbox" checked={!hiddenCols.has(c.k)} onChange={() => toggleCol(c.k)} />
                                                    {c.h}
                                                </label>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <Link to="/"><button className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600">Back</button></Link>
                </div>
            </div>
            <div className="relative shadow-md sm:rounded-lg">
                <DateSessionReportFilter handleFilterChange={handelFilterChange} handleExport={handleExport} handelReportChange={() => {}}
                    handleSubmitFilters={getList} handleClearFilters={() => setFilters({})} multiEvent syncToDate />
                <div className="overflow-auto max-h-[75vh]">
                    <table style={{ borderCollapse: 'separate', borderSpacing: 0, border: SECTION_DIVIDER }} className="w-full text-sm text-left text-gray-500">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky top-0 z-20">
                            <tr>
                                <th className={th} rowSpan={2} style={frozen('date', 3, '#f9fafb')}>Date</th>
                                <th className={th} rowSpan={2} style={frozen('session', 3, '#f9fafb')}>Session</th>
                                <th className={th} rowSpan={2} style={frozen('menu_item', 3, '#f9fafb')}>Menu Item</th>
                                <th className={th} rowSpan={2}>Method</th>
                                {shownGroups.map(g => (
                                    <th key={g.key} className={th} colSpan={g.cols.length} style={{ borderLeft: SECTION_DIVIDER }}>{g.label}</th>
                                ))}
                            </tr>
                            <tr>
                                {shownGroups.map(g => g.cols.map((c, ci) => (
                                    <th key={c.k} className={th} title={c.src}
                                        style={ci === 0 ? { borderLeft: SECTION_DIVIDER } : undefined}>
                                        {c.h}
                                    </th>
                                )))}
                            </tr>
                            {/* Inline per-column filter row (contains / = / ≤ / ≥ / between / not between) */}
                            <ColumnFilterRow
                                cols={filterCols.map(c => ({
                                    key: c.key,
                                    style: {
                                        ...(c.frozen ? frozen(c.key, 3, '#f9fafb') : {}),
                                        ...(c.divider ? { borderLeft: SECTION_DIVIDER } : {}),
                                    },
                                }))}
                                filters={filters}
                                onChange={setColFilter} />
                        </thead>
                        <tbody>
                            {visibleRows.map((r, index) => (
                                <tr key={index} className="bg-lime-100 border border-black-b">
                                    <td className={td} style={frozen('date', 1, '#ecfccb')}>{formatDate(r.date)}</td>
                                    <td className={td} style={frozen('session', 1, '#ecfccb')}>{r.session_name}</td>
                                    <td className="px-2 py-1 border border-black font-medium text-gray-900" style={frozen('menu_item', 1, '#ecfccb')}><ItemName item={r} /></td>
                                    <td className={td}>{r.method}</td>
                                    {shownGroups.map(g => g.cols.map((c, ci) => (
                                        <td key={c.k} className={td} style={ci === 0 ? { borderLeft: SECTION_DIVIDER } : undefined}>{dec(r[c.k], c.d)}</td>
                                    )))}
                                </tr>
                            ))}
                            {!loading && visibleRows.length === 0 && (
                                <tr><td className="px-2 py-4 text-center text-gray-500" colSpan={4 + shownGroups.reduce((a, g) => a + g.cols.length, 0)}>
                                    {activeFilterCols.length ? 'No rows match the filters.' : 'No ticked records found.'}
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default NewMasterReport;
